import { defineComponent } from '@nuxtjs/composition-api';

const __default__ = defineComponent({ head: {} });

import { computed, watch, reactive, ref } from 'vue';
import {
  useRoute,
  useMeta,
  useFetch,
  useRouter,
  useContext,
} from '@nuxtjs/composition-api';

import {
  usePagePreloader,
  useCatalogNav,
  useCategory,
  useCategoryContent,
  usePageAggs,
  useBreadcrumbs,
} from '~/composables';
import { getProductContent } from '~/helpers/vue';
import AppBreadcrumbs from '@/components/AppBreadcrumbs.vue';
import CatalogLayout from '@/domains/catalog/components/CatalogLayout.vue';
import CatalogHead from '@/domains/catalog/components/CatalogHead.vue';
import CatalogProductCard from '@/domains/catalog/components/CatalogProductCard.vue';
import CatalogCategoriesNav from '@/domains/catalog/components/CatalogCategoriesNav.vue';
import CatalogFilter from '@/domains/catalog/components/CatalogFilter.vue';


export default /*#__PURE__*/Object.assign(__default__, {
  __name: '_slug',
  setup(__props) {

const useCatalogSlugPage = () => {
  const router = useRouter();
  const route = useRoute();
  const { error, ssrContext } = useContext();
  const { start: startPageLoading, stop: stopPageLoading } = usePagePreloader();
  const {
    loading: catalogIsLoading,
    start: startCatalogLoading,
    stop: stopCatalogLoading,
  } = usePagePreloader('catalog');

  const currentPage = computed(() =>
    route.value.query.page ? +route.value.query.page : undefined,
  );

  const pageSlug = computed(() => route.value.params.slug);

  const { breadcrumbs, setCrumbs } = useBreadcrumbs();

  const { getPageAggs } = usePageAggs(route.value.query);
  const aggs = reactive(getPageAggs());

  const categoryParams = reactive({});

  const setCategoryParams = () => {
    Object.keys(categoryParams).forEach((key) => delete categoryParams[key]);
    Object.assign(categoryParams, {
      page: currentPage.value,
      limit: aggs.limit,
      ...aggs.filters,
      ...aggs.sorting,
      includes: ['branch', 'children'],
      'order[popular]': 'desc',
    });
  };

  setCategoryParams();

  const {
    fetch: fetchCategory,
    category,
    pagesCount,
  } = useCategory(pageSlug, categoryParams, 'catalog-slug');

  const { categoryAggs, categoryHeader, categoryContent, categoryProducts } =
    useCategoryContent(category);

  const { catalogNavItems, getCatalogNav } = useCatalogNav(category);

  const sortingHasChanged = ref(false);

  const doFetchCategory = async () => {
    startCatalogLoading(!sortingHasChanged.value);
    sortingHasChanged.value = false;
    try {
      setCategoryParams();
      await fetchCategory();
      await getCatalogNav();

      const categoriesTree = category.value.branch.length
        ? category.value.branch.reduceRight((tree, currentBranch, index) => {
            return [
              ...tree,
              {
                text: currentBranch.name,
                link: index > 0 ? `/catalog/${currentBranch.slug}` : undefined,
              },
            ];
          }, [])
        : [
            {
              text: category.value?.name,
            },
          ];

      setCrumbs('main', 'catalog', ...categoriesTree);
    } catch (_) {
      if (ssrContext) ssrContext.res.statusCode = 404;
      else error({ statusCode: 404 });
    } finally {
      stopCatalogLoading();
    }
  };

  const updateRouterQuery = () => {
    router.push({
      query: { ...aggs.filters, ...aggs.sorting, page: currentPage.value },
    });
  };

  const updatePage = async () => {
    updateRouterQuery();
    await doFetchCategory();
  };

  const changeSorting = (value) => {
    aggs.sorting = value;
    sortingHasChanged.value = true;
  };

  watch(aggs, () => {
    updatePage();
  });

  watch(currentPage, () => {
    updatePage();
  });

  useFetch(async () => {
    startPageLoading();
    await doFetchCategory();
    stopPageLoading();
  });

  useMeta(() => ({
    title: category.value?.seo.page_title,
    meta: [
      {
        hid: 'description',
        name: 'description',
        content: category.value?.seo.description,
      },
      {
        property: 'og:title',
        content: category.value?.seo.og_title,
      },
    ],
  }));

  return {
    pageSlug,
    catalogIsLoading,
    breadcrumbs,
    aggs,
    category,
    categoryAggs,
    categoryHeader,
    categoryContent,
    categoryProducts,
    catalogNavItems,
    pagesCount,
    changeSorting,
    currentPage,
  };
};

const {
  pageSlug,
  catalogIsLoading,
  breadcrumbs,
  aggs,
  category,
  categoryAggs,
  categoryHeader,
  categoryContent,
  categoryProducts,
  catalogNavItems,
  pagesCount,
  changeSorting,
  currentPage,
} = useCatalogSlugPage();

return { __sfc: true,useCatalogSlugPage, pageSlug, catalogIsLoading, breadcrumbs, aggs, category, categoryAggs, categoryHeader, categoryContent, categoryProducts, catalogNavItems, pagesCount, changeSorting, currentPage, getProductContent, AppBreadcrumbs, CatalogLayout, CatalogHead, CatalogProductCard, CatalogCategoriesNav, CatalogFilter }
}

})